import { useState, useCallback, useRef, useEffect } from 'react';
import { useProjectStore } from '../../stores/useProjectStore';
import { usePviStore } from '../../stores/usePviStore';
import { useDiagnosticStore } from '../../stores/useDiagnosticStore';
import { useWalletStore } from '../../stores/useWalletStore';
import { useAccountStore } from '../../stores/useAccountStore';
import { rivellumApi } from '../../api/rivellumApi';
import { EditorTabs } from './EditorTabs';
import { MonacoEditor } from './MonacoEditor';
import { WelcomeWorkspace } from './WelcomeWorkspace';
import { ProblemsPanel } from './ProblemsPanel';
import { MIST_LANGUAGE_ID } from '../../editor/mistLanguage';
import { analyze } from '../../editor/diagnosticService';
import { makeDiagnostic } from '../../editor/diagnosticTypes';
import styles from './EditorArea.module.css';

const MIN_PROBLEMS_HEIGHT = 80;
const DEFAULT_PROBLEMS_HEIGHT = 280;
const AUTOSAVE_INTERVAL_MS = 5000;

/** Parse a server-side compile error message into a MistDiagnostic. */
function parseServerCompileError(msg: string, fileName: string) {
  // Try to extract line/column from spans like "Span {start:296, end:300, line:10, column:24}"
  const spanMatch = msg.match(/line:\s*(\d+)\s*,\s*column:\s*(\d+)/i);
  const line = spanMatch ? parseInt(spanMatch[1], 10) : 1;
  const col = spanMatch ? parseInt(spanMatch[2], 10) : 1;

  // Clean up the message — strip "Compilation failed:" prefix if present
  const cleanMsg = msg.replace(/^Compilation failed:\s*/i, '').trim();

  return makeDiagnostic({
    code: 'MIST_SERVER',
    category: 'Build',
    stage: 'BundleBuild',
    severity: 'Error',
    message: cleanMsg,
    detail: `Server compiler rejected this code. The local analyzer may not catch all errors that the full Mist compiler detects.`,
    sourceFile: fileName,
    range: {
      startLine: line,
      startColumn: col,
      endLine: line,
      endColumn: col + 10,
    },
  });
}

export function EditorArea() {
  const activeProject = useProjectStore(s => s.activeProject);
  const openFileIds = useProjectStore(s => s.openFileIds);
  const activeFileId = useProjectStore(s => s.activeFileId);
  const updateFileContent = useProjectStore(s => s.updateFileContent);
  const closeFile = useProjectStore(s => s.closeFile);
  const setActiveFile = useProjectStore(s => s.setActiveFile);
  const saveProject = useProjectStore(s => s.saveProject);

  const togglePviPanel = usePviStore(s => s.togglePanel);
  const pviPanelOpen = usePviStore(s => s.panelOpen);
  const pviCompileResult = usePviStore(s => s.compileResult);
  const setCompileResult = usePviStore(s => s.setCompileResult);

  const diagResult = useDiagnosticStore(s => s.result);
  const phaseStatus = useDiagnosticStore(s => s.phaseStatus);
  const analyzing = useDiagnosticStore(s => s.analyzing);
  const setDiagnostics = useDiagnosticStore(s => s.setDiagnostics);
  const addServerDiagnostics = useDiagnosticStore(s => s.addServerDiagnostics);

  const [showProblems, setShowProblems] = useState(true);
  const [problemsHeight, setProblemsHeight] = useState(DEFAULT_PROBLEMS_HEIGHT);
  const [compileStatus, setCompileStatus] = useState<'idle' | 'compiling' | 'success' | 'error'>('idle');
  const [compiledContentHash, setCompiledContentHash] = useState<string | null>(null);
  const [compiledBundle, setCompiledBundle] = useState<string | null>(null);
  const [deployStatus, setDeployStatus] = useState<'idle' | 'deploying' | 'success' | 'error'>('idle');
  const [outputLog, setOutputLog] = useState<Array<{ time: string; message: string; level: 'info' | 'success' | 'error' }>>([]);
  const [activeBottomTab, setActiveBottomTab] = useState<'problems' | 'output'>('problems');
  const [lastSaved, setLastSaved] = useState<number | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editorRef = useRef<{ revealLine?: (line: number, col: number) => void }>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const resizingProblems = useRef(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);

  const onProblemsResizeStart = useCallback((e: React.MouseEvent) => {
    resizingProblems.current = true;
    dragStartY.current = e.clientY;
    dragStartHeight.current = problemsHeight;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }, [problemsHeight]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizingProblems.current) return;
      const delta = dragStartY.current - e.clientY;
      const containerHeight = areaRef.current?.clientHeight ?? 600;
      const maxHeight = containerHeight * 0.75;
      const newHeight = Math.max(MIN_PROBLEMS_HEIGHT, Math.min(maxHeight, dragStartHeight.current + delta));
      setProblemsHeight(newHeight);
    };
    const onMouseUp = () => {
      if (resizingProblems.current) {
        resizingProblems.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const activeFile = activeProject?.files.find(f => f.id === activeFileId) ?? null;
  const openFiles = activeProject?.files.filter(f => openFileIds.includes(f.id)) ?? [];

  const hasOpenFiles = openFiles.length > 0;
  const isMist = activeFile?.language === MIST_LANGUAGE_ID;

  const diagnostics = diagResult.diagnostics;
  const errorCount = diagnostics.filter(d => d.severity === 'Error').length;
  const warningCount = diagnostics.filter(d => d.severity === 'Warning').length;
  const pviNodeCount = pviCompileResult.graph?.nodes.length ?? 0;

  // Compile is dirty if content changed since last successful compile
  const isCompileCurrent = compiledContentHash !== null && activeFile?.content === compiledContentHash;
  const canDeploy = isCompileCurrent && compileStatus === 'success' && errorCount === 0 && !diagResult.isDeployBlocked;

  // Reset compile status when code changes after a compile
  useEffect(() => {
    if ((compileStatus === 'success' || compileStatus === 'error') && !isCompileCurrent) {
      setCompileStatus('idle');
      setCompiledBundle(null);
    }
  }, [activeFile?.content, compileStatus, isCompileCurrent]);

  // Auto-save every 5 seconds when there are modified files
  useEffect(() => {
    const timer = setInterval(() => {
      const project = useProjectStore.getState().activeProject;
      if (project && project.files.some(f => f.isModified)) {
        saveProject().then(() => setLastSaved(Date.now()));
      }
    }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [saveProject]);

  const handleSave = useCallback(() => {
    setSaveStatus('saving');
    saveProject().then(() => {
      setLastSaved(Date.now());
      setSaveStatus('saved');
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
    });
  }, [saveProject]);

  const appendLog = useCallback((message: string, level: 'info' | 'success' | 'error' = 'info') => {
    const time = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setOutputLog(prev => [...prev, { time, message, level }]);
  }, []);

  const handleCompile = useCallback(async () => {
    if (!activeFile) return;
    setCompileStatus('compiling');
    setCompiledBundle(null);
    setShowProblems(true);
    setActiveBottomTab('output');
    appendLog(`Compiling ${activeFile.name}…`);

    // 1. Run local analysis for diagnostics + PVI graph
    const { result, pviResult } = analyze({ source: activeFile.content, fileName: activeFile.name });
    const errors = result.diagnostics.filter(d => d.severity === 'Error');
    const warnings = result.diagnostics.filter(d => d.severity === 'Warning');
    setDiagnostics(result.diagnostics, pviResult, activeFile.name);
    setCompileResult(pviResult);

    if (errors.length > 0 || pviResult.status === 'error') {
      setCompileStatus('error');
      setCompiledContentHash(activeFile.content);
      const parts: string[] = [];
      if (errors.length > 0) parts.push(`${errors.length} error${errors.length !== 1 ? 's' : ''}`);
      if (warnings.length > 0) parts.push(`${warnings.length} warning${warnings.length !== 1 ? 's' : ''}`);
      setActiveBottomTab('problems');
      appendLog(`Compilation failed — ${parts.join(', ')}`, 'error');
      return;
    }

    // 2. Server-side compile to get a deployable bundle
    try {
      const compileResult = await rivellumApi.compileMist(activeFile.content);
      setCompiledBundle(compileResult.bundle);
      setCompiledContentHash(activeFile.content);
      setCompileStatus('success');
      const parts: string[] = [];
      if (warnings.length > 0) parts.push(`${warnings.length} warning${warnings.length !== 1 ? 's' : ''}`);
      parts.push(`${compileResult.bundle_size} bytes`);
      if (compileResult.contract_name) parts.push(compileResult.contract_name);
      appendLog(`Compiled successfully — ${parts.join(', ')}`, 'success');
    } catch (err) {
      setCompiledContentHash(activeFile.content);
      setCompileStatus('error');
      const msg = err instanceof Error ? err.message : String(err);

      // Parse server error into a proper diagnostic for the Problems panel
      const serverDiag = parseServerCompileError(msg, activeFile.name);
      addServerDiagnostics([serverDiag]);

      setActiveBottomTab('problems');
      appendLog(`Server compile failed: ${msg}`, 'error');
    }
  }, [activeFile, setDiagnostics, setCompileResult, appendLog]);

  const handleDeploy = useCallback(async () => {
    if (!canDeploy || !compiledBundle || !activeFile) return;
    setDeployStatus('deploying');
    setShowProblems(true);
    setActiveBottomTab('output');
    appendLog(`Deploying ${activeFile.name}…`);

    let walletStore = useWalletStore.getState();

    // Auto-connect wallet if not connected (user is already authenticated in console)
    if (walletStore.connectionStatus !== 'connected' || !walletStore.activeAccount) {
      appendLog('Connecting wallet…');
      const connected = await walletStore.connect();
      walletStore = useWalletStore.getState();
      if (!connected || !walletStore.activeAccount) {
        setDeployStatus('error');
        appendLog('Deploy failed: could not connect wallet. Please ensure Natos Wallet is unlocked.', 'error');
        setTimeout(() => setDeployStatus('idle'), 5000);
        return;
      }
    }

    try {
      // 1. Ask wallet to sign the deploy transaction
      const projectName = useProjectStore.getState().activeProject?.name ?? 'contract';
      appendLog('Estimating fee…');

      let feeRivlDisplay = '2.0000 RIVL';
      let feeUsdDisplay = '$2.00';
      try {
        const est = await rivellumApi.estimateFee(activeFile.content);
        feeRivlDisplay = est.rivl_fee_display || feeRivlDisplay;
        feeUsdDisplay = est.total_fee_display || feeUsdDisplay;
      } catch {
        // use fallback
      }

      appendLog('Requesting wallet approval…');
      const deployIntent: Record<string, unknown> = {
        sender: walletStore.activeAccount.address,
        recipient: '0x' + '0'.repeat(64),
        amount: 0,
        memo: `deploy:${projectName}`,
        data: JSON.stringify({
          type: 'contract_deploy',
          fee_rivl_display: feeRivlDisplay,
          fee_usd_display: feeUsdDisplay,
        }),
      };

      const signResult = await walletStore.signIntent(deployIntent);
      if (!signResult) {
        const walletError = useWalletStore.getState().error;
        setDeployStatus('error');
        appendLog(`Deploy failed: ${walletError ?? 'transaction rejected by wallet'}`, 'error');
        setTimeout(() => setDeployStatus('idle'), 5000);
        return;
      }

      appendLog('Approved. Submitting deploy to network…');

      // 2. Submit the compiled bundle to the node
      const deployResult = await rivellumApi.deployMist({
        bundle: compiledBundle,
        init_args: [],
        deployer: walletStore.activeAccount.address,
      });

      setDeployStatus('success');
      const cid = deployResult.contract_id ? ` — ${deployResult.contract_id.slice(0, 12)}…` : '';
      const feeInfo = deployResult.fee_display ? ` | fee: ${deployResult.fee_display}` : '';
      appendLog(`Deployed successfully${cid}${feeInfo}`, 'success');

      useAccountStore.getState().addDeployment({
        id: crypto.randomUUID(),
        projectId: useProjectStore.getState().activeProject?.id ?? '',
        projectName: projectName,
        txHash: deployResult.intent_id ?? '',
        network: 'testnet',
        status: 'finalized',
        timestamp: Date.now(),
        contractAddress: deployResult.contract_id,
      });

      setTimeout(() => { setDeployStatus('idle'); }, 8000);
    } catch (err) {
      setDeployStatus('error');
      const msg = err instanceof Error ? err.message : String(err);
      appendLog(`Deploy failed: ${msg}`, 'error');
      setTimeout(() => { setDeployStatus('idle'); }, 6000);
    }
  }, [canDeploy, compiledBundle, activeFile, appendLog]);

  const handleGoToLine = useCallback((line: number, _column: number) => {
    editorRef.current?.revealLine?.(line, _column);
  }, []);

  if (!activeProject || !hasOpenFiles) {
    return (
      <div className={styles.area}>
        {hasOpenFiles && (
          <EditorTabs
            files={openFiles}
            activeFileId={activeFileId}
            onSelect={setActiveFile}
            onClose={closeFile}
          />
        )}
        <div className={styles.welcomeContainer}>
          <WelcomeWorkspace />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.area} ref={areaRef}>
      <EditorTabs
        files={openFiles}
        activeFileId={activeFileId}
        onSelect={setActiveFile}
        onClose={closeFile}
      />

      {/* Compile / Deploy toolbar */}
      {isMist && activeFile && (
        <div className={styles.actionToolbar}>
          <button
            className={`${styles.actionBtn} ${styles.saveBtn} ${saveStatus === 'saved' ? styles.actionBtnSuccess : ''}`}
            onClick={handleSave}
            disabled={saveStatus === 'saving'}
            title="Save project (Ctrl+S)"
          >
            {saveStatus === 'saved' ? '✅ Saved' : saveStatus === 'saving' ? '💾 Saving…' : '💾 Save'}
          </button>
          <button
            className={`${styles.actionBtn} ${styles.compileBtn} ${compileStatus === 'compiling' ? styles.actionBtnBusy : ''} ${compileStatus === 'success' ? styles.actionBtnSuccess : ''} ${compileStatus === 'error' ? styles.actionBtnError : ''}`}
            onClick={handleCompile}
            disabled={compileStatus === 'compiling'}
          >
            {compileStatus === 'compiling' ? '⟳ Compiling…' : compileStatus === 'success' ? '✔ Compiled' : compileStatus === 'error' ? '✖ Error' : '▶ Compile'}
          </button>
          <button
            className={`${styles.actionBtn} ${styles.deployBtn} ${deployStatus === 'deploying' ? styles.actionBtnBusy : ''} ${deployStatus === 'success' ? styles.actionBtnSuccess : ''} ${deployStatus === 'error' ? styles.actionBtnError : ''}`}
            onClick={handleDeploy}
            disabled={!canDeploy || deployStatus === 'deploying'}
            title={!isCompileCurrent ? 'Compile first before deploying' : errorCount > 0 ? 'Fix all errors before deploying' : 'Deploy contract to network'}
          >
            {deployStatus === 'deploying' ? '⟳ Deploying…' : deployStatus === 'success' ? '✔ Deployed' : deployStatus === 'error' ? '✖ Failed' : '🚀 Deploy'}
          </button>
          {!canDeploy && compileStatus !== 'success' && compileStatus !== 'compiling' && (
            <span className={styles.actionHint}>Compile before deploying</span>
          )}
          {!canDeploy && compileStatus === 'success' && errorCount > 0 && (
            <span className={styles.actionHint}>Fix {errorCount} error{errorCount !== 1 ? 's' : ''} to deploy</span>
          )}
          {lastSaved && (
            <span className={styles.actionHint} style={{ marginLeft: 'auto', opacity: 0.5 }}>
              Saved {new Date(lastSaved).toLocaleTimeString()}
            </span>
          )}
        </div>
      )}

      <div className={styles.editorContainer}>
        {activeFile ? (
          <MonacoEditor
            key={activeFile.id}
            file={activeFile}
            onChange={(content) => updateFileContent(activeFile.id, content)}
            onSave={handleSave}
          />
        ) : (
          <div className={styles.noFileSelected}>
            <span>Select a file to edit</span>
          </div>
        )}
      </div>

      {/* Status bar */}
      {isMist && activeFile && (
        <div className={styles.statusBar}>
          <span className={styles.statusLang}>Mist</span>

          {/* Phase-aware status */}
          {analyzing && (
            <span className={`${styles.statusChip} ${styles.statusAnalyzing}`}>Analyzing…</span>
          )}
          {!analyzing && errorCount > 0 && (
            <button className={`${styles.statusChip} ${styles.statusError}`} onClick={() => setShowProblems(true)}>
              ✖ {errorCount} error{errorCount !== 1 ? 's' : ''}
            </button>
          )}
          {!analyzing && warningCount > 0 && (
            <button className={`${styles.statusChip} ${styles.statusWarning}`} onClick={() => setShowProblems(true)}>
              ⚠ {warningCount} warning{warningCount !== 1 ? 's' : ''}
            </button>
          )}
          {!analyzing && errorCount === 0 && warningCount === 0 && diagnostics.length === 0 && (
            <span className={`${styles.statusChip} ${styles.statusOk}`}>✔ Clean</span>
          )}

          {/* Phase label */}
          {!analyzing && phaseStatus !== 'Clean' && phaseStatus !== 'WarningsOnly' && (
            <span className={`${styles.statusChip} ${styles.statusPhase}`}>
              {phaseStatus === 'SyntaxError' ? 'Syntax error' :
               phaseStatus === 'CompileError' ? 'Compile error' :
               phaseStatus === 'AnalysisError' ? 'Analysis error' :
               phaseStatus === 'BuildError' ? 'Build error' :
               phaseStatus === 'DeployBlocked' ? 'Deploy blocked' :
               phaseStatus === 'SimulationBlocked' ? 'Simulation blocked' : ''}
            </span>
          )}

          <button
            className={styles.statusToggle}
            onClick={() => setShowProblems(p => !p)}
            title={showProblems ? 'Hide Problems' : 'Show Problems'}
          >
            Problems
          </button>
          <button
            className={`${styles.statusToggle} ${pviPanelOpen ? styles.statusToggleActive : ''}`}
            onClick={togglePviPanel}
            title={pviPanelOpen ? 'Hide PVI Graph' : 'Show PVI Graph'}
          >
            PVI {pviNodeCount > 0 ? `(${pviNodeCount})` : ''}
          </button>
        </div>
      )}

      {/* Problems Panel with resize handle */}
      {showProblems && isMist && (
        <>
          <div
            className={styles.problemsResizeHandle}
            onMouseDown={onProblemsResizeStart}
          />
          <div className={styles.problemsContainer} style={{ height: problemsHeight }}>
            <ProblemsPanel
              diagnostics={diagnostics}
              fileName={activeFile?.name ?? null}
              onClose={() => setShowProblems(false)}
              onGoToLine={handleGoToLine}
              analyzing={analyzing}
              outputLog={outputLog}
              activeTab={activeBottomTab}
              onTabChange={setActiveBottomTab}
              onClearLog={() => setOutputLog([])}
            />
          </div>
        </>
      )}
    </div>
  );
}
